import { useEffect, useState } from "react";
import { chipLabel, HOUSE_LABELS, weekdayDate, type RowSuggestions, type SuggestionChip } from "../../shared/clash-suggestions";
import { conflictSlotDates, encodeConflictDates, isHouseChoice, slotLabel } from "../../shared/venue-conflicts";
import type { DaySlot, InterestDate, VenueChoice } from "../../shared/tasting";
import { api } from "../api";

/** What a picked chip changes: the row's date and/or slot, and/or the card's house. */
export type SuggestionPick = { index: number; date?: string; slot?: DaySlot | null; venue?: VenueChoice };

/** Live suggestions for the chosen house and dates; refetches 300 ms after a change. */
export function useClashSuggestions(venue: VenueChoice | null, rows: { date?: string | null; slot?: DaySlot | null }[], excludeEvent: string | null, excludeVisit: string | null): RowSuggestions[] {
  const [result, setResult] = useState<RowSuggestions[]>([]);
  const encoded = encodeConflictDates(conflictSlotDates(rows));
  const key = `${venue ?? ""}|${encoded}|${excludeEvent ?? ""}|${excludeVisit ?? ""}`;
  useEffect(() => {
    if (!isHouseChoice(venue) || !encoded) {
      setResult([]);
      return;
    }
    let cancel = false;
    const params = new URLSearchParams({ venue, dates: encoded });
    if (excludeEvent) params.set("exclude_event", excludeEvent);
    if (excludeVisit) params.set("exclude_visit", excludeVisit);
    const timer = window.setTimeout(() => {
      void api<{ rows: RowSuggestions[] }>(`/api/tastings/suggestions?${params.toString()}`)
        .then((data) => {
          if (!cancel) setResult(data.rows);
        })
        .catch(() => {
          if (!cancel) setResult([]);
        });
    }, 300);
    return () => {
      cancel = true;
      window.clearTimeout(timer);
    };
  }, [key]);
  return result;
}

function Chip({ chip, group, disabled, onPick }: { chip: SuggestionChip; group: "same_date" | "other_slot" | "nearby"; disabled: boolean; onPick: () => void }) {
  const soft = chip.soft ? ` · ${chip.soft} ενδιαφέρον` : "";
  return (
    <button type="button" className={chip.soft ? "suggest-chip soft" : "suggest-chip"} disabled={disabled} onClick={onPick} title={chip.soft ? "Ελεύθερο, αλλά υπάρχει ενδιαφέρον άλλου πελάτη (όχι κλεισμένο)" : "Ελεύθερο"}>
      {chipLabel(chip, group)}
      {soft ? <small>{soft}</small> : null}
    </button>
  );
}

/**
 * «Προτάσεις διαθεσιμότητας» between «Ημερομηνίες ενδιαφέροντος» and «Χώρος & Καλεσμένοι»:
 * one block per chosen date that clashes. A booked event or closure gets the full block; a date with
 * only other leads gets a softer line with the same quick picks behind «Δες εναλλακτικές».
 */
export function ClashSuggestions({ suggestions, rows, venue, canEdit, onPick }: { suggestions: RowSuggestions[]; rows: InterestDate[]; venue: VenueChoice | null; canEdit: boolean; onPick: (pick: SuggestionPick) => void }) {
  const [openSoft, setOpenSoft] = useState<Set<string>>(new Set());
  if (!isHouseChoice(venue) || !suggestions.length) return null;
  const house = HOUSE_LABELS[venue];
  const blocks = suggestions
    .map((entry) => ({ entry, index: rows.findIndex((row) => row.date === entry.date) }))
    .filter((item) => item.index >= 0)
    .sort((left, right) => left.index - right.index);
  if (!blocks.length) return null;
  return (
    <section className="panel clash-suggest" aria-label="Προτάσεις διαθεσιμότητας">
      <h2>Προτάσεις διαθεσιμότητας</h2>
      {blocks.map(({ entry, index }) => {
        const key = `${entry.date}|${entry.slot ?? ""}`;
        const when = [weekdayDate(entry.date), slotLabel(entry.slot)].filter(Boolean).join(" ");
        const hard = entry.level === "hard";
        const names = entry.blockers.filter((item) => (hard ? item.kind === "closed" : true)).map((item) => item.name);
        const leads = entry.blockers.filter((item) => item.kind === "interest").length;
        const expanded = hard || openSoft.has(key);
        const empty = !entry.same_date.length && !entry.other_slot.length && !entry.nearby.length;
        const pick = (chip: SuggestionChip, group: "same_date" | "other_slot" | "nearby") => {
          if (group === "same_date") onPick({ index, venue: chip.venue, ...(chip.slot !== entry.slot ? { slot: chip.slot } : {}) });
          else if (group === "other_slot") onPick({ index, slot: chip.slot });
          else onPick({ index, date: chip.date, ...(chip.slot !== entry.slot ? { slot: chip.slot } : {}) });
        };
        return (
          <div className={hard ? "suggest-block hard" : "suggest-block soft"} key={key}>
            <p className="suggest-head" role={hard ? "alert" : "status"}>
              {hard ? "⛔" : "ⓘ"} <strong>{house} {when}</strong>
              {hard
                ? ` — ${entry.closed ? "κλειστό (μπλοκάρισμα χώρου)" : `κλεισμένο: ${names.join(", ")}`}${leads ? ` · +${leads} ενδιαφέρον` : ""}`
                : ` — ${leads} ενδιαφέρον άλλου πελάτη, όχι κλεισμένο`}
              {rows.length > 1 ? <span className="suggest-row-tag">{index === 0 ? "κύρια ημερομηνία" : `εναλλακτική ${index}`}</span> : null}
            </p>
            {!hard ? (
              <button type="button" className="suggest-toggle" onClick={() => setOpenSoft((current) => { const next = new Set(current); if (next.has(key)) next.delete(key); else next.add(key); return next; })}>
                {expanded ? "Απόκρυψη εναλλακτικών" : "Δες εναλλακτικές"}
              </button>
            ) : null}
            {expanded ? (
              empty ? <p className="suggest-empty">Δεν βρέθηκε ελεύθερη εναλλακτική κοντά σε αυτή την ημερομηνία.</p> : (
                <div className="suggest-groups">
                  {entry.same_date.length ? (
                    <div className="suggest-group"><span className="suggest-label">Ίδια μέρα, άλλο κτήμα</span>
                      <div className="suggest-chips">{entry.same_date.map((chip) => <Chip key={chip.venue + (chip.slot ?? "")} chip={chip} group="same_date" disabled={!canEdit} onPick={() => pick(chip, "same_date")} />)}</div>
                    </div>
                  ) : null}
                  {entry.other_slot.length ? (
                    <div className="suggest-group"><span className="suggest-label">Ίδια μέρα στο {house}, άλλη ώρα</span>
                      <div className="suggest-chips">{entry.other_slot.map((chip) => <Chip key={chip.slot ?? "x"} chip={chip} group="other_slot" disabled={!canEdit} onPick={() => pick(chip, "other_slot")} />)}</div>
                    </div>
                  ) : null}
                  {entry.nearby.length ? (
                    <div className="suggest-group"><span className="suggest-label">Κοντινές ελεύθερες ημερομηνίες στο {house}</span>
                      <div className="suggest-chips">{entry.nearby.map((chip) => <Chip key={chip.date + (chip.slot ?? "")} chip={chip} group="nearby" disabled={!canEdit} onPick={() => pick(chip, "nearby")} />)}</div>
                    </div>
                  ) : null}
                  {entry.same_date.length ? <p className="suggest-note">Η αλλαγή κτήματος ισχύει για όλες τις ημερομηνίες της κάρτας.</p> : null}
                </div>
              )
            ) : null}
          </div>
        );
      })}
    </section>
  );
}
